/* storybook-check-ignore */
import Head from 'next/head';

import type { Article, Organization, Person, WithContext } from 'schema-dts';

import {
  IArticleFields,
  IAuthor,
  IAuthorFields,
  IEditorFields,
  IOpenGraphArticleFields,
  IOpenGraphFields,
  IPublisherFields,
  IReviewerFields,
  ISeoBaseFields,
  ITwitterCardFields,
} from '../../../declarations/contentful';
import { getSocialLink } from '../../../helpers/socialLinks';

interface ArticleSchemaProps {
  slug: string;
  title: string | undefined;
  subtitle?: string | undefined;
  description?: string | undefined;
  authors: IAuthorFields[];
  editors?: IEditorFields[];
  reviewers?: IReviewerFields[];
  image: string | undefined;
  publisher: IPublisherFields | undefined;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
  section?: string;
}

const getBaseTags = (props: { base?: ISeoBaseFields }) => {
  if (!props.base || !props.base.fields) {
    return null;
  }
  const { canonicalUrl, metaDescription, title } = props.base.fields;
  return [
    <title key="title">{title}</title>,
    <meta key="description" name="description" content={metaDescription} />,
    <link key="canonical" rel="canonical" href={canonicalUrl} />,
  ];
};

const getOgTags = (props: {
  og?: IOpenGraphFields;
  ogArticle?: IOpenGraphArticleFields;
  authors?: IAuthor[];
  createdAt: string;
  updatedAt: string;
}) => {
  if (!props.og || !props.ogArticle || !props.og.fields || !props.ogArticle.fields) {
    return null;
  }
  const { url, type, title, description, image, siteName } = props.og.fields;
  // TODO: may need to double check what time format these are in
  const {
    // fallback to article created at and updated at if not explicitly
    // provided
    publishedTime = props.createdAt,
    modifiedTime = props.updatedAt,
    expirationDate,
    section,
    tags,
  } = props.ogArticle.fields;
  return [
    <meta key="og:title" property="og:title" content={title} />,
    <meta key="og:type" property="og:type" content={type} />,
    <meta key="og:url" property="og:url" content={url} />,
    <meta key="og:image" property="og:image" content={image} />,
    <meta key="og:description" property="og:description" content={description} />,
    <meta key="og:site_name" property="og:site_name" content={siteName} />,
    <meta
      key="og:article:published_time"
      property="og:article:published_time"
      content={publishedTime}
    />,
    <meta
      key="og:article:modified_time"
      property="og:article:modified_time"
      content={modifiedTime}
    />,
    <meta key="og:article:section" property="og:article:section" content={section} />,
    expirationDate && (
      <meta
        key="og:article:expiration_time"
        property="og:article:expiration_time"
        content={expirationDate}
      />
    ),
    (props.authors || []).map((author) => (
      <meta
        key={author.fields.displayName}
        property="og:article:author"
        content={author.fields.displayName}
      />
    )),
    (tags || []).map((tag) => <meta key={tag} property="og:article:tag" content={tag} />),
  ];
};

const getTwitterTags = (props: { card?: ITwitterCardFields }) => {
  if (!props.card || !props.card.fields) {
    return null;
  }
  const { cardType, site, creatorTwitterHandle, description, image, title } = props.card.fields;
  return [
    <meta key="twitter:card" name="twitter:card" content={cardType} />,
    <meta key="twitter:site" name="twitter:site" content={site} />,
    <meta key="twitter:creator" name="twitter:creator" content={creatorTwitterHandle} />,
    <meta key="twitter:title" name="twitter:title" content={title} />,
    <meta key="twitter:description" name="twitter:description" content={description} />,
    <meta key="twitter:image" name="twitter:image" content={image} />,
  ];
};

const getCrawlerTags = (props: { index: boolean; follow: boolean }) => {
  const { follow, index } = props;
  if (follow && index) {
    return null;
  }
  return [
    <meta
      key="robots"
      name="robots"
      content={[!follow && 'nofollow', !index && 'noindex'].filter(Boolean).join(',')}
    />,
  ];
};

// Export only for testing
export const getArticleSchemaData = ({
  slug,
  title,
  subtitle,
  description,
  authors = [],
  editors,
  reviewers,
  image,
  publisher,
  createdAt,
  updatedAt,
  tags,
  section,
}: ArticleSchemaProps): WithContext<Article> => {
  const canonicalUrl = `https://www.opendoor.com${slug}`;

  // Normalize dates to ISO 8601 datetime format
  // publishDate from Contentful is date-only, sys timestamps are full datetime
  const normalizedPublishedDate = createdAt.includes('T')
    ? createdAt
    : `${createdAt}T00:00:00.000Z`;

  const schemaData: WithContext<Article> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': canonicalUrl,
    mainEntityOfPage: canonicalUrl,
    dateModified: updatedAt,
    datePublished: normalizedPublishedDate,
    // TODO: use custom image for each article page
    image: [
      image,
      'https://images.opendoor.com/source/s3/imgdrop-production/1afd9b4404c54cd5bd4d3737eec0d70d.jpg?preset=square-2048',
    ].filter(Boolean) as string[],
    inLanguage: 'en-US',
  };

  if (title) schemaData.headline = title;
  if (subtitle) schemaData.alternativeHeadline = subtitle;
  if (description) schemaData.description = description;
  if (tags && tags.length > 0) schemaData.keywords = tags;
  if (section) schemaData.about = { '@type': 'Thing', name: section };

  // Map authors to Person schema
  const authorPersons: Person[] = authors.map((author) => {
    const sameAs: string[] = [];
    if (author.twitterUsername) {
      sameAs.push(getSocialLink('twitter', author.twitterUsername));
    }
    if (author.linkedInUsername) {
      sameAs.push(getSocialLink('linkedin', author.linkedInUsername));
    }
    if (author.instagramUsername) {
      sameAs.push(getSocialLink('instagram', author.instagramUsername));
    }
    const url = author.website || sameAs?.[0];

    const person: Person = {
      '@type': 'Person',
      name: author.displayName,
    };

    if (url) person.url = url;
    if (sameAs.length > 0) person.sameAs = sameAs;

    return person;
  });

  if (reviewers && reviewers.length > 0) {
    reviewers.forEach((reviewer) => {
      const reviewerPerson: Person = {
        '@type': 'Person',
        name: reviewer.displayName,
      };
      if (reviewer.linkedInUsername) {
        reviewerPerson.sameAs = getSocialLink('linkedin', reviewer.linkedInUsername);
      }
      authorPersons.push(reviewerPerson);
    });
  }

  if (authorPersons.length > 0) {
    schemaData.author = authorPersons;
  }

  if (editors && editors.length > 0) {
    const editor = editors[0];
    const editorPerson: Person = {
      '@type': 'Person',
      name: editor.displayName,
    };
    if (editor.linkedInUsername) {
      editorPerson.sameAs = getSocialLink('linkedin', editor.linkedInUsername);
    }
    schemaData.editor = editorPerson;
  }

  if (publisher) {
    const publisherSameAs: string[] = [];
    if (publisher.twitterUsername) {
      publisherSameAs.push(getSocialLink('twitter', publisher.twitterUsername));
    }
    if (publisher.linkedInUsername) {
      publisherSameAs.push(`https://www.linkedin.com/company/${publisher.linkedInUsername}`);
    }
    if (publisher.instagramUsername) {
      publisherSameAs.push(getSocialLink('instagram', publisher.instagramUsername));
    }
    if (publisher.youtubeChannel) {
      publisherSameAs.push(`https://www.youtube.com/c/${publisher.youtubeChannel}`);
    }
    if (publisher.facebookUsername) {
      publisherSameAs.push(getSocialLink('facebook', publisher.facebookUsername));
    }
    const publisherUrl = publisher.url || publisherSameAs?.[0];

    const publisherOrg: Organization = {
      '@type': 'Organization',
      name: publisher.name,
    };

    if (publisherUrl) publisherOrg.url = publisherUrl;
    if (publisherSameAs.length > 0) publisherOrg.sameAs = publisherSameAs;
    if (publisher.logoUrl) {
      publisherOrg.logo = {
        '@type': 'ImageObject',
        url: publisher.logoUrl,
      };
    }

    schemaData.publisher = publisherOrg;
  }

  return schemaData;
};

interface SeoHeadProps {
  authors: IArticleFields['authors'];
  base: IArticleFields['seoBase'];
  editors?: IArticleFields['editors'];
  ogArticle: IArticleFields['openGraphArticle'];
  reviewers?: IArticleFields['reviewers'];
  slug: string;
  sys: { createdAt: string; updatedAt: string };
  twitterCard: IArticleFields['twitterCard'];
  articleName?: string;
  articleSubtitle?: string;
  headerSummary?: string;
  og?: IArticleFields['openGraph'];
  publishDate?: IArticleFields['publishDate'];
  publisher?: IArticleFields['publisher'];
}

const SeoHead = ({
  slug,
  articleName,
  articleSubtitle,
  headerSummary,
  base,
  authors = [],
  editors = [],
  reviewers = [],
  og,
  ogArticle,
  twitterCard,
  sys,
  publisher,
  publishDate,
}: SeoHeadProps) => {
  // ensure that you don't nest tags inside of fragments
  // if you do that, then the next head component
  // will have issues with overrides
  const fields = base?.fields || {};
  return (
    <Head>
      {getBaseTags({ base })}
      {getOgTags({
        og,
        ogArticle,
        createdAt: sys.createdAt,
        updatedAt: sys.updatedAt,
        authors,
      })}
      {getTwitterTags({ card: twitterCard })}
      {getCrawlerTags({ index: fields.index ?? true, follow: fields.follow ?? true })}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getArticleSchemaData({
              slug,
              title: fields.title || articleName,
              subtitle: articleSubtitle,
              description: headerSummary || fields.metaDescription,
              authors: authors.map((author) => author.fields),
              editors: editors.map((editor) => editor.fields),
              reviewers: reviewers.map((reviewer) => reviewer.fields),
              image: og?.fields.image,
              publisher: publisher?.fields,
              createdAt: publishDate || sys.createdAt,
              updatedAt: sys.updatedAt,
              tags: ogArticle?.fields.tags,
              section: ogArticle?.fields.section,
            }),
          ),
        }}
      />
    </Head>
  );
};

export default SeoHead;
